import React, { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Box,
  createStyles,
  Divider,
  Link,
  makeStyles,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import { CustomDialog } from '../common/custom-dialog';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import { toSentenceCase } from '../../../../util/format';
import SearchAccount from '../common/search-account/SearchAccount';
import Tabs from '../../../tabs';
import clsx from 'clsx';
import { AccountWithOutageInfo } from '../../types';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      borderRadius: 0,
      padding: 5,
    },
    accountLink: {
      fontSize: '1rem',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    accountData: {},
    selectedAccount: {
      backgroundColor: colors.lightYellow,
    },
    dot: {
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: colors.roseRed,
      display: 'inline-block',
      marginRight: 10,
    },
  }),
);

interface SwitchAccountDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  accountListWithOutageInfo: AccountWithOutageInfo[];
}

export const SwitchAccountDialog = ({
  open,
  setOpen,
  accountListWithOutageInfo,
}: SwitchAccountDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    selectedAccountForOutage,
    setSelectedAccountForOutage,
  } = useOutageLeftPanelState();
  const [searchAccountQuery, setSearchAccountQuery] = useState<string>('');

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelectAccount = (account: AccountWithOutageInfo) => {
    setSelectedAccountForOutage({ ...account });
    setOpen(false);
    setSearchAccountQuery('');
  };

  const handleSearch = useCallback((query: string) => {
    setSearchAccountQuery(query.trim().toLowerCase());
  }, []);

  const filteredAccounts = useMemo(() => {
    if (!searchAccountQuery) {
      return accountListWithOutageInfo;
    }
    return accountListWithOutageInfo.filter(a =>
      a.accountNumber?.toLowerCase().includes(searchAccountQuery),
    );
  }, [searchAccountQuery, accountListWithOutageInfo]);

  const accountsWithOutages = useMemo(
    () => filteredAccounts.filter(a => a.hasKnownOutage),
    [filteredAccounts],
  );
  const accountsWithNoOutages = useMemo(
    () => filteredAccounts.filter(a => !a.hasKnownOutage),
    [filteredAccounts],
  );
  return (
    <Fragment>
      <CustomDialog
        open={open}
        onClose={handleClose}
        title={toSentenceCase(t('ALL_ACCOUNTS'))}
      >
        <Box p={2.5}>
          <Box mb={1}>
            <SearchAccount onSearch={handleSearch} />
          </Box>

          <Box>
            <Paper className={classes.paper}>
              {accountsWithOutages.length > 0 ? (
                <Tabs
                  tabHeight="60px"
                  noTabPanelPadding={true}
                  tabs={[
                    {
                      label: `${accountsWithOutages.length} ${t('OUTAGES')}`,
                      icon: (
                        <span className={classes.dot} aria-hidden="true"></span>
                      ),
                      data: (
                        <>
                          {accountsWithOutages.map(account => (
                            <AccountItem
                              key={account.accountNumber}
                              account={account}
                              selected={
                                selectedAccountForOutage?.accountNumber ===
                                account.accountNumber
                              }
                              onSelect={handleSelectAccount}
                            />
                          ))}
                        </>
                      ),
                    },
                    {
                      label: t('NO_OUTAGES'),
                      data: (
                        <>
                          {accountsWithNoOutages.map(account => (
                            <AccountItem
                              key={account.accountNumber}
                              account={account}
                              selected={
                                selectedAccountForOutage?.accountNumber ===
                                account.accountNumber
                              }
                              onSelect={handleSelectAccount}
                            />
                          ))}
                        </>
                      ),
                    },
                  ]}
                />
              ) : (
                <Box width={'inherit'}>
                  {filteredAccounts.map(account => (
                    <AccountItem
                      key={account.accountNumber}
                      account={account}
                      selected={
                        selectedAccountForOutage?.accountNumber ===
                        account.accountNumber
                      }
                      onSelect={handleSelectAccount}
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </CustomDialog>
    </Fragment>
  );
};

const AccountItem: React.FC<{
  account: AccountWithOutageInfo;
  selected: boolean;
  onSelect: (account: AccountWithOutageInfo) => void;
}> = ({ account, selected, onSelect }) => {
  const classes = useStyles();
  return (
    <Fragment key={account.accountNumber}>
      <Box
        p={2.5}
        display="flex"
        flexDirection="column"
        gridGap={10}
        className={clsx(classes.accountData, {
          [classes.selectedAccount]: selected,
        })}
      >
        <Link className={classes.accountLink} onClick={() => onSelect(account)}>
          {account.accountNumber}
        </Link>
        <Typography variant="body2">{account.addressLocation}</Typography>
      </Box>
      <Divider />
    </Fragment>
  );
};
